import { create } from "@bufbuild/protobuf";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { watchlistServiceClient } from "@/connect";
import { CACHE_CONFIG } from "@/lib/react-query";
import type { ListWatchlistsRequest } from "@/types/proto/api/v1/watchlist_service_pb";
import { ListWatchlistsRequestSchema, WatchlistSchema } from "@/types/proto/api/v1/watchlist_service_pb";

// Query keys factory for consistent cache management
export const watchlistKeys = {
  all: ["watchlists"] as const,
  lists: () => [...watchlistKeys.all, "list"] as const,
  list: (filters: Partial<ListWatchlistsRequest>) => [...watchlistKeys.lists(), filters] as const,
  details: () => [...watchlistKeys.all, "detail"] as const,
  detail: (id: string) => [...watchlistKeys.details(), id] as const,
  tickers: (watchlistId: string) => [...watchlistKeys.all, "tickers", watchlistId] as const,
};

/**
 * Hook to fetch all watchlists for the current user
 */
export function useWatchlists() {
  return useQuery({
    queryKey: watchlistKeys.list({}),
    queryFn: async () => {
      const response = await watchlistServiceClient.listWatchlists(create(ListWatchlistsRequestSchema, {}));
      return response.watchlists;
    },
    ...CACHE_CONFIG.MODERATE,
  });
}

/**
 * Hook to fetch tickers in a specific watchlist or all tickers
 * @param watchlistId - The watchlist ID (use "all" or null for all tickers)
 * @param options - Filter and sort options
 */
export function useTickersByWatchlist(
  watchlistId: string | null,
  options?: {
    filterActionable?: boolean;
    filterHolding?: boolean;
    filterValued?: boolean;
    orderBy?: string;
    orderDirection?: string;
    pageToken?: string;
    pageSize?: number;
    skipToast?: boolean; // Skip global error Toast (for custom error UI)
  },
) {
  return useQuery({
    queryKey: watchlistId ? ["watchlists", "tickers", watchlistId, options] : ["tickers", options],
    queryFn: async () => {
      // Unified: use ListTickers for both "all" and specific watchlist
      const { tickerServiceClient } = await import("@/connect");
      const response = await tickerServiceClient.listTickers({
        watchlistId: watchlistId || "",
        filterActionable: options?.filterActionable ?? false,
        filterHolding: options?.filterHolding ?? false,
        filterValued: options?.filterValued ?? false,
        orderBy: options?.orderBy || "actionability",
        orderDirection: options?.orderDirection || "desc",
        pageToken: options?.pageToken ?? "",
        pageSize: options?.pageSize ?? 20,
      });
      return response;
    },
    enabled: !!watchlistId,
    meta: {
      skipToast: options?.skipToast ?? false, // Allow skipping global Toast
    },
    ...CACHE_CONFIG.MODERATE,
  });
}

/**
 * Hook to create a new watchlist
 */
export function useCreateWatchlist() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (name: string) => {
      const response = await watchlistServiceClient.createWatchlist({
        watchlist: create(WatchlistSchema, { name }),
      });
      return response.watchlist;
    },
    onSuccess: () => {
      // Invalidate watchlist queries
      queryClient.invalidateQueries({ queryKey: watchlistKeys.all });
    },
    retry: false, // Don't retry on failure to avoid duplicate requests
  });
}

/**
 * Hook to update a watchlist (e.g., rename)
 */
export function useUpdateWatchlist() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, name }: { id: string; name: string }) => {
      const response = await watchlistServiceClient.updateWatchlist({
        name: `watchlists/${id}`,
        newName: name,
        updateMask: { paths: ["name"] },
      });
      return response.watchlist;
    },
    onSuccess: () => {
      // Invalidate watchlist queries
      queryClient.invalidateQueries({ queryKey: watchlistKeys.all });
    },
    retry: false, // Don't retry on failure to avoid duplicate requests
  });
}

/**
 * Hook to delete a watchlist
 */
export function useDeleteWatchlist() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await watchlistServiceClient.deleteWatchlist({
        name: `watchlists/${id}`,
      });
      return id;
    },
    onSuccess: () => {
      // Invalidate watchlist queries
      queryClient.invalidateQueries({ queryKey: watchlistKeys.all });
    },
    retry: false, // Don't retry on failure to avoid duplicate requests
  });
}

/**
 * Hook to add a symbol to a watchlist (auto-creates ticker if not exists)
 */
export function useAddSymbolToWatchlist() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ watchlistId, symbol }: { watchlistId: string; symbol: string }) => {
      const response = await watchlistServiceClient.addSymbolToWatchlist({
        watchlistId,
        symbol,
      });
      return response.ticker;
    },
    onSuccess: (_, variables) => {
      // Invalidate watchlist list to update tickerCount for all watchlists
      queryClient.invalidateQueries({ queryKey: watchlistKeys.all });
      // Invalidate watchlist tickers for the specific watchlist
      queryClient.invalidateQueries({ queryKey: watchlistKeys.tickers(variables.watchlistId) });
      // Invalidate all tickers query
      queryClient.invalidateQueries({ queryKey: ["tickers"] });
    },
    retry: false, // Don't retry on failure to avoid duplicate requests
  });
}

/**
 * Hook to remove a ticker from a watchlist
 */
export function useRemoveTickerFromWatchlist() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ watchlistId, tickerId }: { watchlistId: string; tickerId: string }) => {
      await watchlistServiceClient.removeTickerFromWatchlist({
        watchlistId,
        tickerId,
      });
      return { watchlistId, tickerId };
    },
    onSuccess: (_, variables) => {
      // Invalidate watchlist list to update tickerCount for all watchlists
      queryClient.invalidateQueries({ queryKey: watchlistKeys.all });
      // Invalidate watchlist tickers for the specific watchlist
      queryClient.invalidateQueries({ queryKey: watchlistKeys.tickers(variables.watchlistId) });
      // Invalidate all tickers query
      queryClient.invalidateQueries({ queryKey: ["tickers"] });
    },
    retry: false, // Don't retry on failure to avoid duplicate requests
  });
}
